/**
 * User config key under which the date/time format preference is persisted
 * (see {@link DateTimeFormatPreference}). Stored per-user via the config
 * service at `v1/config/user/key/date_time_format_preference`.
 */
export const DATE_TIME_FORMAT_CONFIG_KEY = "date_time_format_preference";

/**
 * Named date presets the user can choose from.
 *
 * - `long` — the browser locale with a spelled-out month (e.g. `June 30, 2026`;
 *   `30. Juni 2026` under a German locale).
 * - `system` — the browser locale, all-numeric (e.g. `6/30/2026`).
 * - `iso` — ISO 8601, `YYYY-MM-DD` (e.g. `2026-06-30`).
 * - `swiss` — Swiss/European, `DD.MM.YYYY` (e.g. `30.06.2026`).
 * - `us` — US, `MM/DD/YYYY` (e.g. `06/30/2026`).
 * - `uk` — UK, `DD/MM/YYYY` (e.g. `30/06/2026`).
 *
 * `long` and `system` follow the locale's own component order and separators;
 * the rest fix both (see {@link NUMERIC_DATE_PATTERNS}).
 */
export type DatePreset = "long" | "system" | "iso" | "swiss" | "us" | "uk";

/**
 * Clock convention for the time component.
 *
 * - `system` — defer to the browser locale's 12/24-hour convention.
 * - `12h` — 12-hour clock with an AM/PM marker.
 * - `24h` — 24-hour clock.
 */
export type ClockPreference = "system" | "12h" | "24h";

/**
 * A user's date/time display preference. Controls component ordering,
 * separators and the clock convention — never which fields a given screen
 * shows (a screen always asks for the granularity it needs, see
 * {@link FormatOptions}).
 */
export interface DateTimeFormatPreference {
  datePreset: DatePreset;
  clock: ClockPreference;
}

/**
 * The preference applied before the user has chosen one: the locale's own
 * ordering with a spelled-out month, which is what the app displayed before
 * the preference existed.
 */
export const DEFAULT_DATE_TIME_FORMAT_PREFERENCE: DateTimeFormatPreference = {
  datePreset: "long",
  clock: "system",
};

/**
 * How much of the time component to render. The date component is always
 * present.
 *
 * - `none` — date only.
 * - `minutes` — date + `HH:mm` (default for most metadata timestamps).
 * - `seconds` — date + `HH:mm:ss`.
 * - `millis` — date + `HH:mm:ss.SSS` (precision/log displays).
 */
export type TimePrecision = "none" | "minutes" | "seconds" | "millis";

/**
 * A zone a formatter can read instants in other than the viewer's local one.
 * See {@link resolveFormats}.
 */
export type DisplayZone = "UTC";

/** Options accepted by {@link ResolvedDateTimeFormat.format}. */
export interface FormatOptions {
  /** Time granularity to append after the date. Defaults to `"minutes"`. */
  time?: TimePrecision;
  /** When true, append the short name of the formatter's zone (e.g. `GMT+2`, `UTC`). */
  timeZone?: boolean;
  /**
   * When true and the instant falls on today in the formatter's zone,
   * render only the time component — the chat-caption convention where a
   * same-day date is noise. Ignored when `time` is `"none"`.
   */
  omitDateIfToday?: boolean;
}

/**
 * The date and time components of a formatted timestamp, kept separate for
 * stacked layouts (e.g. a table cell that renders the date on one line and the
 * time on the next). See {@link ResolvedDateTimeFormat.formatParts}.
 */
export interface DateTimeParts {
  /** The date component, honoring the active preset. Never empty. */
  date: string;
  /**
   * The time component (with any requested timezone suffix), or `""` when
   * `options.time === "none"`.
   */
  time: string;
}

/**
 * A preference resolved into a concrete formatter. Computed once per
 * preference change (see {@link resolveFormats}) and cached in the registry;
 * `format` is cheap to call per render.
 */
export interface ResolvedDateTimeFormat {
  /** The preference this was resolved from. */
  readonly preference: DateTimeFormatPreference;
  /**
   * The zone every instant is read in; `undefined` is the viewer's local
   * zone. Fixed at resolve time, so callers never pass a zone per call.
   */
  readonly zone: DisplayZone | undefined;
  /** Format a `Date` according to the preference and the given options. */
  format(date: Date, options?: FormatOptions): string;
  /**
   * Like {@link format}, but returns the date and time components separately
   * so a caller can stack them on distinct lines. Both honor the same
   * preference; joining `date` and `time` yields the same information as
   * {@link format} (the exact connector may differ, since `format` defers to
   * the locale for the `long` and `system` presets while stacked callers
   * supply their own layout).
   */
  formatParts(date: Date, options?: FormatOptions): DateTimeParts;
  /**
   * Like {@link format} with seconds, but the fractional-second field is
   * rendered from `fraction` — the caller's own zero-padded digit string —
   * rather than from the `Date`'s milliseconds. `""` renders no fraction and
   * no decimal separator.
   *
   * The substitution happens on `Intl.DateTimeFormat.formatToParts` output, so
   * the locale's own decimal separator and field order survive a width `Intl`
   * would refuse (it caps `fractionalSecondDigits` at 3). That is the point:
   * epoch nanoseconds carry six more digits than a `Date` can hold, and
   * `formatDateTimeNsWithOptions` is what splits them out.
   */
  formatFractional(
    date: Date,
    fraction: string,
    options?: FractionalFormatOptions,
  ): string;
}

/**
 * Options for {@link ResolvedDateTimeFormat.formatFractional}. Deliberately
 * narrower than {@link FormatOptions}: the caller's `fraction` already fixes
 * the granularity at seconds-plus-digits, and a fractional-second reading is
 * never the same-day shorthand `omitDateIfToday` exists for.
 */
export type FractionalFormatOptions = Pick<FormatOptions, "timeZone">;

/** The date presets that fix their own component order and separator. */
type NumericDatePreset = Exclude<DatePreset, "long" | "system">;

type DateField = "year" | "month" | "day";

interface NumericDatePattern {
  /** The three date fields in the order they are written. */
  readonly order: readonly [DateField, DateField, DateField];
  /** Placed between each pair of fields. */
  readonly separator: string;
}

/** Component order and separator for each fixed (non-locale) preset. */
const NUMERIC_DATE_PATTERNS: Record<NumericDatePreset, NumericDatePattern> = {
  iso: { order: ["year", "month", "day"], separator: "-" },
  swiss: { order: ["day", "month", "year"], separator: "." },
  us: { order: ["month", "day", "year"], separator: "/" },
  uk: { order: ["day", "month", "year"], separator: "/" },
};

/**
 * The locale the fixed presets are rendered through. They define their own
 * component order, so all they need from `Intl` is each field as a padded
 * number and an English AM/PM marker — none of which should shift with the
 * viewer's locale, or `30.06.2026` would come back reordered.
 */
const FIXED_LOCALE = "en-US";

/**
 * Reads the year, month and day of a date as padded numbers in the
 * formatter's zone. Shared by every fixed preset, which then arranges them
 * itself.
 */
const NUMERIC_FIELDS_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
};

function numericDateFields(
  formatter: FormatterCache,
  date: Date,
): Record<DateField, string> {
  const parts = formatter("fields", NUMERIC_FIELDS_OPTIONS).formatToParts(date);
  const read = (field: DateField): string =>
    parts.find((part) => part.type === field)?.value ?? "";
  return { year: read("year"), month: read("month"), day: read("day") };
}

/**
 * Whether the browser locale uses a 12-hour clock. Used to resolve a
 * `clock: "system"` preference under a fixed date preset (those presets have no
 * inherent clock convention, so we borrow the locale's).
 */
function localeUses12Hour(): boolean {
  try {
    return (
      new Intl.DateTimeFormat(undefined, { hour: "numeric" }).resolvedOptions()
        .hour12 ?? false
    );
  } catch {
    return false;
  }
}

/** Short name of `zone` at `date` (e.g. `GMT+2` for the browser's, `UTC`). */
function shortTimeZoneName(date: Date, zone: DisplayZone | undefined): string {
  try {
    const part = new Intl.DateTimeFormat(undefined, {
      timeZoneName: "short",
      timeZone: zone,
    })
      .formatToParts(date)
      .find((p) => p.type === "timeZoneName");
    return part?.value ?? "";
  } catch {
    return "";
  }
}

/** Resolve a clock preference to an `Intl` `hour12` value (undefined = locale). */
function resolveHour12(clock: ClockPreference): boolean | undefined {
  if (clock === "12h") {
    return true;
  }
  if (clock === "24h") {
    return false;
  }
  return undefined;
}

/**
 * `Intl` options for the time component at a given precision, under a clock
 * convention already resolved to 12-hour (`true`), 24-hour (`false`) or
 * whatever the locale prefers (`undefined`).
 */
function timeFormatOptions(
  time: TimePrecision,
  hour12: boolean | undefined,
): Intl.DateTimeFormatOptions {
  const options: Intl.DateTimeFormatOptions = {
    // A 24-hour clock pads to `08:05`; a 12-hour one reads `8:05 AM`.
    hour: hour12 === false ? "2-digit" : "numeric",
    minute: "2-digit",
  };
  if (hour12 === true) {
    options.hour12 = true;
  } else if (hour12 === false) {
    // `hourCycle: "h23"` rather than `hour12: false`, which some locales are
    // free to resolve to `h24` — rendering midnight as `24:00`.
    options.hourCycle = "h23";
  }
  if (time === "seconds" || time === "millis") {
    options.second = "2-digit";
  }
  if (time === "millis") {
    options.fractionalSecondDigits = 3;
  }
  return options;
}

/**
 * `Intl` options for a time component whose fractional seconds the caller will
 * substitute. Asking for a width at all is only how we get the locale's
 * decimal separator and a `fractionalSecond` part to swap into — the digits
 * `Intl` puts there are always discarded, which is what lets a caller render
 * more of them than `Intl`'s cap of three.
 */
function fractionalTimeOptions(
  fraction: string,
  hour12: boolean | undefined,
): Intl.DateTimeFormatOptions {
  const options = timeFormatOptions("seconds", hour12);
  if (fraction !== "") {
    options.fractionalSecondDigits = Math.min(fraction.length, 3) as 1 | 2 | 3;
  }
  return options;
}

/** Cache key distinguishing the option sets {@link fractionalTimeOptions} builds. */
function fractionalTimeKey(fraction: string, timeZone: boolean): string {
  return `fractional:${Math.min(fraction.length, 3)}:${timeZone}`;
}

/**
 * Formats `date`'s time component with `fraction` standing in for whatever
 * fractional-second digits the formatter produced.
 */
function timeWithFraction(
  formatter: Intl.DateTimeFormat,
  date: Date,
  fraction: string,
): string {
  return formatter
    .formatToParts(date)
    .map((part) => (part.type === "fractionalSecond" ? fraction : part.value))
    .join("");
}

/**
 * A per-format cache of `Intl.DateTimeFormat` instances, keyed by the caller's
 * option set. Constructing one is expensive relative to formatting with it, and
 * these run once per rendered timestamp (a single grid page is hundreds), while
 * only a handful of distinct option sets ever occur. The cache lives as long as
 * the resolved format that owns it, so it is discarded when the preference
 * changes. Every formatter it builds reads instants in `zone`, which is how
 * the zone reaches every option set without each caller adding it.
 */
type FormatterCache = (
  key: string,
  options: Intl.DateTimeFormatOptions,
) => Intl.DateTimeFormat;

function formatterCache(
  locale: string | undefined,
  zone: DisplayZone | undefined,
): FormatterCache {
  const cache = new Map<string, Intl.DateTimeFormat>();
  return (key, options) => {
    let formatter = cache.get(key);
    if (formatter === undefined) {
      formatter = new Intl.DateTimeFormat(locale, {
        ...options,
        timeZone: zone,
      });
      cache.set(key, formatter);
    }
    return formatter;
  };
}

/** Build a formatter for a preset with a fixed component order. */
function buildPatternFormat(
  preference: DateTimeFormatPreference,
  pattern: NumericDatePattern,
  zone: DisplayZone | undefined,
): ResolvedDateTimeFormat {
  // A fixed component order carries no clock convention of its own, so a
  // `system` clock borrows the locale's.
  const hour12 = resolveHour12(preference.clock) ?? localeUses12Hour();
  const timeFormatter = formatterCache(FIXED_LOCALE, zone);

  function formatParts(date: Date, options: FormatOptions = {}): DateTimeParts {
    const time = options.time ?? "minutes";
    const fields = numericDateFields(timeFormatter, date);
    let dateStr = pattern.order
      .map((field) => fields[field])
      .join(pattern.separator);
    let timeStr =
      time === "none"
        ? ""
        : timeFormatter(time, timeFormatOptions(time, hour12)).format(date);
    if (options.timeZone) {
      const tz = shortTimeZoneName(date, zone);
      if (tz) {
        // Annotate the clock reading with the zone; fall back to the date line
        // when there is no time component.
        if (timeStr) {
          timeStr = `${timeStr} ${tz}`;
        } else {
          dateStr = `${dateStr} ${tz}`;
        }
      }
    }
    return { date: dateStr, time: timeStr };
  }

  return {
    preference,
    zone,
    formatParts,
    format(date, options) {
      const { date: dateStr, time: timeStr } = formatParts(date, options);
      return timeStr ? `${dateStr} ${timeStr}` : dateStr;
    },
    formatFractional(date, fraction, options = {}) {
      const fields = numericDateFields(timeFormatter, date);
      const dateStr = pattern.order
        .map((field) => fields[field])
        .join(pattern.separator);
      let timeStr = timeWithFraction(
        timeFormatter(
          fractionalTimeKey(fraction, options.timeZone === true),
          fractionalTimeOptions(fraction, hour12),
        ),
        date,
        fraction,
      );
      if (options.timeZone) {
        const tz = shortTimeZoneName(date, zone);
        if (tz) {
          timeStr = `${timeStr} ${tz}`;
        }
      }
      return `${dateStr} ${timeStr}`;
    },
  };
}

/**
 * Build a locale-driven formatter (the `long` and `system` presets), which
 * differ only in how the month is written.
 */
function buildLocaleFormat(
  preference: DateTimeFormatPreference,
  month: "long" | "numeric",
  zone: DisplayZone | undefined,
): ResolvedDateTimeFormat {
  const hour12 = resolveHour12(preference.clock);
  // `undefined` locale defers to the browser's.
  const formatter = formatterCache(undefined, zone);
  const dateOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month,
    day: "numeric",
  };

  return {
    preference,
    zone,
    format(date, options = {}) {
      const time = options.time ?? "minutes";
      const opts: Intl.DateTimeFormatOptions = { ...dateOptions };
      if (time !== "none") {
        Object.assign(opts, timeFormatOptions(time, hour12));
      }
      if (options.timeZone) {
        opts.timeZoneName = "short";
      }
      return formatter(
        `full:${time}:${options.timeZone === true}`,
        opts,
      ).format(date);
    },
    formatParts(date, options = {}) {
      const time = options.time ?? "minutes";
      if (time === "none") {
        // No time line — the zone (if any) annotates the date instead.
        const opts: Intl.DateTimeFormatOptions = { ...dateOptions };
        if (options.timeZone) {
          opts.timeZoneName = "short";
        }
        return {
          date: formatter(`date:${options.timeZone === true}`, opts).format(
            date,
          ),
          time: "",
        };
      }
      const timeOpts = timeFormatOptions(time, hour12);
      if (options.timeZone) {
        timeOpts.timeZoneName = "short";
      }
      return {
        date: formatter("date:false", dateOptions).format(date),
        time: formatter(
          `time:${time}:${options.timeZone === true}`,
          timeOpts,
        ).format(date),
      };
    },
    formatFractional(date, fraction, options = {}) {
      // One `Intl` call over date *and* time, so the locale keeps its own
      // connector between them ("June 30, 2026 at 2:05:09.468476035 PM")
      // exactly as `format` does — the substitution only touches the digits.
      const opts: Intl.DateTimeFormatOptions = {
        ...dateOptions,
        ...fractionalTimeOptions(fraction, hour12),
      };
      if (options.timeZone) {
        opts.timeZoneName = "short";
      }
      const key = `full:${fractionalTimeKey(fraction, options.timeZone === true)}`;
      return timeWithFraction(formatter(key, opts), date, fraction);
    },
  };
}

/**
 * Resolve a {@link DateTimeFormatPreference} into a concrete formatter that
 * reads every instant in `zone` (the viewer's local zone when omitted). The
 * preference decides how a timestamp is written and the zone which clock it
 * is read on; the two compose.
 *
 * The `long` and `system` presets follow the browser locale via `Intl`; every
 * other preset fixes the component order and separator itself. Nothing here
 * depends on a date-formatting library — see this module's README.
 */
export function resolveFormats(
  preference: DateTimeFormatPreference,
  zone?: DisplayZone,
): ResolvedDateTimeFormat {
  const base = buildBaseFormat(preference, zone);
  return {
    ...base,
    format(date, options = {}) {
      if (
        options.omitDateIfToday &&
        (options.time ?? "minutes") !== "none" &&
        isTodayIn(date, zone)
      ) {
        return base.formatParts(date, options).time;
      }
      return base.format(date, options);
    },
  };
}

function buildBaseFormat(
  preference: DateTimeFormatPreference,
  zone: DisplayZone | undefined,
): ResolvedDateTimeFormat {
  if (preference.datePreset === "long") {
    return buildLocaleFormat(preference, "long", zone);
  }
  if (preference.datePreset === "system") {
    return buildLocaleFormat(preference, "numeric", zone);
  }
  return buildPatternFormat(
    preference,
    NUMERIC_DATE_PATTERNS[preference.datePreset],
    zone,
  );
}

/** Whether `date` falls on the current calendar day in `zone` (local when `undefined`). */
function isTodayIn(date: Date, zone: DisplayZone | undefined): boolean {
  if (zone === undefined) {
    return isToday(date);
  }
  const now = new Date();
  return (
    date.getUTCFullYear() === now.getUTCFullYear() &&
    date.getUTCMonth() === now.getUTCMonth() &&
    date.getUTCDate() === now.getUTCDate()
  );
}

/** Whether `date` falls on the current calendar day in the viewer's local zone. */
export function isToday(date: Date): boolean {
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

/**
 * Every date preset, in the order the settings UI offers them (locale-driven
 * first, then the fixed orderings). Also the accept-list
 * {@link normalizePreference} validates against.
 */
export const DATE_PRESETS: readonly DatePreset[] = [
  "long",
  "system",
  "iso",
  "swiss",
  "us",
  "uk",
];
const CLOCK_PREFERENCES: readonly ClockPreference[] = ["system", "12h", "24h"];

/**
 * Coerce an arbitrary persisted value (which may be `null`, partial, or
 * malformed JSON from an older client) into a valid
 * {@link DateTimeFormatPreference}, falling back to
 * {@link DEFAULT_DATE_TIME_FORMAT_PREFERENCE} for any unrecognized field.
 *
 * This is also the migration path for a preference stored by a client that
 * offered a `custom` preset with a user-supplied token string: `custom` is no
 * longer a recognized preset, so such a value reads back as the default.
 */
export function normalizePreference(value: unknown): DateTimeFormatPreference {
  if (value === null || typeof value !== "object") {
    return DEFAULT_DATE_TIME_FORMAT_PREFERENCE;
  }
  const candidate = value as Partial<
    Record<keyof DateTimeFormatPreference, unknown>
  >;
  const datePreset = DATE_PRESETS.includes(candidate.datePreset as DatePreset)
    ? (candidate.datePreset as DatePreset)
    : DEFAULT_DATE_TIME_FORMAT_PREFERENCE.datePreset;
  const clock = CLOCK_PREFERENCES.includes(candidate.clock as ClockPreference)
    ? (candidate.clock as ClockPreference)
    : DEFAULT_DATE_TIME_FORMAT_PREFERENCE.clock;
  return { datePreset, clock };
}
